import React from 'react';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import './Breadcrumbs.css';

const ROUTE_CONFIG = [
  { path: '/', labelKey: 'breadcrumbs.dashboard', parent: null },
  { path: '/customers', labelKey: 'breadcrumbs.customers', parent: '/' },
  {
    path: '/customers/:customerId',
    labelKey: 'breadcrumbs.customerDetail',
    parent: '/customers',
    dynamic: true,
  },
  { path: '/conversations', labelKey: 'breadcrumbs.conversations', parent: '/' },
  { path: '/users', labelKey: 'breadcrumbs.users', parent: '/' },
  { path: '/users/new', labelKey: 'breadcrumbs.newUser', parent: '/users' },
  {
    path: '/users/edit/:userId',
    labelKey: 'breadcrumbs.editUser',
    parent: '/users',
    dynamic: true,
  },
  { path: '/subscription', labelKey: 'breadcrumbs.subscription', parent: '/' },
  { path: '/plan-selection', labelKey: 'breadcrumbs.planSelection', parent: '/subscription' },
  { path: '/settings', labelKey: 'breadcrumbs.settings', parent: '/' },
];

function findMatchingConfig(pathname) {
  // Exact match first
  const exact = ROUTE_CONFIG.find((r) => r.path === pathname);
  if (exact) return exact;

  // Dynamic match
  for (const route of ROUTE_CONFIG) {
    if (route.path.includes(':')) {
      const match = matchPath({ path: route.path, end: true }, pathname);
      if (match) return route;
    }
  }
  return null;
}

function buildTrail(pathname, t, dynamicLabel) {
  const config = findMatchingConfig(pathname);
  if (!config) return [];

  const trail = [];
  let current = config;

  while (current) {
    trail.unshift({
      path: current.path === config.path ? pathname : current.path,
      label:
        current.path === config.path && current.dynamic && dynamicLabel
          ? dynamicLabel
          : t(current.labelKey),
      isCurrent: current.path === config.path,
    });

    if (current.parent === null) break;
    // eslint-disable-next-line no-loop-func
    current = ROUTE_CONFIG.find((r) => r.path === current.parent);
  }

  return trail;
}

function Breadcrumbs() {
  const location = useLocation();
  const { t } = useTranslation();
  const { dynamicLabel } = useBreadcrumb();

  // Don't render on dashboard
  if (location.pathname === '/') return null;

  const trail = buildTrail(location.pathname, t, dynamicLabel);
  if (trail.length === 0) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <ol className="breadcrumbs-list">
        {trail.map((crumb, index) => (
          <li key={crumb.path} className="breadcrumbs-item">
            {index > 0 && (
              <span className="breadcrumbs-separator material-icons">chevron_right</span>
            )}
            {crumb.isCurrent ? (
              <span className="breadcrumbs-current">{crumb.label}</span>
            ) : (
              <Link to={crumb.path} className="breadcrumbs-link">
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default Breadcrumbs;
